import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import Textarea from 'react-textarea-autosize';
import { Section, SectionTitle } from '@repo/components/containers';
import { Select, SelectOrAddValue } from '@repo/components/forms';
import styles from './ProfileFormField.module.scss';

type FieldType = 'text' | 'select' | 'repeater' | 'toggle' | 'year' | 'date' | 'birthdate';

interface FormField {
  type: FieldType;
  label: string;
  key: string;
  value?: any;
  size?: number;
  prefix?: string;
  form?: Array<FormField>; // if field is a repeater
  options?: { [key: string]: string };
  order?: Array<string>;
  placeholder?: string;
}

interface FormSection {
  label: string;
  type: 'section';
  fields: Array<FormField>;
}

export const ProfileFormContext = createContext<{ contactIsDeceased: boolean }>({
  contactIsDeceased: false,
});

const yearRange = buildYears();
const months = buildMonths();
const dayRange = [...Array(31).keys()].map((x) => x + 1);

function determineType(type) {
  switch (type) {
    case 'all_locations':
      return 'Location';
    case 'all_organizations':
      return 'Organization';
    case 'all_websites':
      return 'Website';
    case 'all_education':
      return 'Education';
  }

  return '';
}

function buildYears() {
  let year = 1900;
  const end = new Date().getFullYear() + 1;
  const years = [];

  while (year <= end) {
    years.push(year);
    year += 1;
  }

  years.reverse();
  return years;
}

function buildMonths() {
  const date = new Date();
  date.setDate(1);
  date.setMonth(0);
  const months = {};

  while (true) {
    months[date.getMonth() + 1] = date.toLocaleDateString('default', { month: 'long' });

    if (date.getMonth() >= 11) {
      break;
    }

    date.setMonth(date.getMonth() + 1);
  }

  return months;
}

type GroupProps = React.ComponentPropsWithoutRef<'div'> & { item: FormField };

function Group({ className, item, ...props }: GroupProps) {
  const { size } = item;
  return (
    <div
      className={classNames(className, 'form-group', size ? `form-group-${size}` : null)}
      {...props}
    />
  );
}

const offsetToggleKeys = ['send_birthday_notification', 'send_memorialized_notification'];
const dependentOnDeceasedKeys = ['memorialized_at', 'send_memorialized_notification'];

export function ProfileFormField({ item, ...props }) {
  const formCtx = useContext(ProfileFormContext);

  if (offsetToggleKeys.includes(item.key)) {
    props = { ...props, offsetToggle: true };
  }

  if (dependentOnDeceasedKeys.includes(item.key) && !formCtx.contactIsDeceased) {
    return null;
  }

  switch (item.type) {
    case 'section':
      return <FormSection item={item} {...props} />;
    case 'text':
      return <Text item={item} {...props} />;
    case 'select':
      return <Picker item={item} {...props} />;
    case 'dropdown':
      return <Dropdown item={item} {...props} />;
    case 'repeater':
      return <Repeater item={item} {...props} />;
    case 'toggle':
      return <Toggle item={item} {...props} />;
    case 'year':
      return <Year item={item} {...props} />;
    case 'date':
      return <DateForm item={item} {...props} />;
    case 'birthdate':
      return <DateSelector item={item} {...props} />;
  }

  return null;
}

const valueToInt = (val: string): number | null => {
  return val == '' ? null : parseInt(val);
};

function DatePartSelect({ value, onChange, options, notSetLabel }) {
  const optionsWithNotSet = useMemo(
    () => [{ value: '', label: notSetLabel }, ...options],
    [options, notSetLabel],
  );

  const selectedOption = useMemo(() => {
    return optionsWithNotSet.find((option) => option.value == value);
  }, [value, optionsWithNotSet]);

  return (
    <div className="form-group form-group-3 date-part-select">
      <span className="form-select-container">
        <Select
          className={styles.select}
          value={selectedOption}
          options={optionsWithNotSet}
          onChange={onChange}
          placeholder={notSetLabel}
          isSearchable
        />
      </span>
    </div>
  );
}

function DateSelector({ item, ...props }) {
  const data = props.data || {};
  const current = data[item.key] ?? item.value ?? { month: null, day: null, year: null };

  const onChange = (unit: 'month' | 'day' | 'year') => (option) => {
    props.onChange(item.key, { ...current, [unit]: valueToInt(option.value) });
  };

  const monthOptions = useMemo(
    () =>
      Object.entries(months).map(([key]) => ({
        value: key,
        label: key.length === 1 ? `0${key}` : key,
      })),
    [],
  );
  const dayOptions = useMemo(
    () =>
      dayRange.map((day) => ({ value: day, label: String(day).length === 1 ? `0${day}` : day })),
    [],
  );
  const yearOptions = useMemo(() => yearRange.map((year) => ({ value: year, label: year })), []);

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form">
        <DatePartSelect
          value={current.month ?? ''}
          onChange={onChange('month')}
          options={monthOptions}
          notSetLabel="MM"
        />
        <DatePartSelect
          value={current.day ?? ''}
          onChange={onChange('day')}
          options={dayOptions}
          notSetLabel="DD"
        />
        <DatePartSelect
          value={current.year ?? ''}
          onChange={onChange('year')}
          options={yearOptions}
          notSetLabel="YYYY"
        />
      </div>
    </Group>
  );
}

function FormSection({ item, ...props }) {
  return (
    <Section>
      <SectionTitle underline>{item.label}</SectionTitle>
      <div className="form">
        {item.fields.map((field, i) => (
          <ProfileFormField key={i} item={field} {...props} />
        ))}
      </div>
    </Section>
  );
}

function Repeater({ item, ...props }) {
  const userUpdates = (props.data || {})[item.key];
  const repeaterEntries = userUpdates || item.data || []; // use the user updates if any have been made

  function handleChange(i, subItem, key, value) {
    const entriesCopy = repeaterEntries.map((entry) => ({ ...(entry || {}) })); // make a shallow copy of the current data to avoid mutating state directly

    if (subItem.type === 'toggle' && value === true) {
      // unset true values from all other items in the repeater for this same key
      for (const entry of entriesCopy) {
        entry[key] = false;
      }
    }

    entriesCopy[i][key] = value;
    props.onChange(item.key, entriesCopy);
  }

  function handleRemove(i, e) {
    e.preventDefault();
    props.onChange(item.key, repeaterEntries.slice(0, i).concat(repeaterEntries.slice(i + 1))); // remove the entry at index i
  }

  function handleAdd(e) {
    e.preventDefault();
    props.onChange(item.key, [...repeaterEntries, {}]);
  }

  return (
    <Section
      className={classNames(
        'form-group',
        ['all_locations', 'all_websites'].includes(item.key) && 'padded-repeater-group',
      )}
    >
      <SectionTitle className="form-repeater-title" underline>
        {item.title}
      </SectionTitle>
      <div className="form-repeater">
        {repeaterEntries.map((dataItem, dataIndex) => (
          <div className="form-repeater-item form" key={dataIndex}>
            {item.form.map((field, i) => (
              <ProfileFormField
                key={i}
                item={{
                  ...field,
                  value: dataItem[field.key],
                }}
                {...props}
                data={dataItem}
                onChange={handleChange.bind(null, dataIndex, field)}
              />
            ))}
            <div className="form-repeater-actions">
              <button
                className="btn-none form-repeater-action form-repeater-remove"
                onClick={handleRemove.bind(null, dataIndex)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}

        <div className="form-repeater-actions form-repeater-footer">
          <button className="btn-none form-repeater-action" onClick={handleAdd}>
            Add {determineType(item.key)}
          </button>
        </div>
      </div>
    </Section>
  );
}

function Text({ item, ...props }) {
  const key = item.key;
  const data = props.data || {};

  const inputProps = {
    className: classNames(
      'form-input',
      data[key] && data[key] !== item.value ? 'form-input-active' : null,
    ),
    value: props.cleared ? data[key] || '' : data[key] ?? item.value ?? '',
    name: key,
    onChange: handleInputChange.bind(null, item, props.onChange),
  };

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      {key === 'bio' ? <Textarea {...inputProps} /> : <input {...inputProps} type="text" />}
    </Group>
  );
}

function Picker({ item, ...props }) {
  const key = item.key;
  const data = props.data || {};
  const active = props.cleared ? data[key] || null : data[key] || item.value;
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(!item.order?.includes(active) ? active || '' : '');
  }, [active]);

  function handleSelect(key) {
    props.onChange(item.key, key);
  }

  function handleInput(e) {
    setValue(e.target.value);
    props.onChange(item.key, e.target.value);
  }

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form-button-group-container">
        <div className="form-button-group">
          {Object.entries(item.options).map(([key, value]) => (
            <button
              className={classNames(
                'form-button-group-item',
                active === key ? 'form-button-group-item-active' : null,
              )}
              onClick={handleSelect.bind(null, key)}
              type="button"
              key={key}
            >
              {value}
            </button>
          ))}
        </div>
        {item.custom && (active === item.custom || value.length > 0) && (
          <input
            onChange={handleInput}
            value={value}
            placeholder={item.placeholder}
            className="form-input form-button-group-input"
            type="text"
          />
        )}
      </div>
    </Group>
  );
}

function Dropdown({ item, ...props }) {
  const currentValue = props.data[item.key] || item.value || null;
  const options = useMemo(() => {
    const isCustomized = currentValue && !item.options.includes(currentValue);
    return [item.clear, isCustomized ? currentValue : '', ...item.options]
      .filter(Boolean)
      .map((value) => ({ value: value === item.clear ? '' : value, label: value }));
  }, [currentValue, item.options]);
  const selectedOption = useMemo(
    () =>
      options.find((option) => (currentValue ? option.value == currentValue : option.value == '')),
    [currentValue, options],
  );

  const onChange = (option) => {
    if (option.value == item.clear) {
      props.onChange(item.key, null);
    } else {
      props.onChange(item.key, option.value);
    }
  };

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <span className="form-select-container">
        <SelectOrAddValue
          className={styles.select}
          options={options}
          value={selectedOption}
          onChange={onChange}
          placeholder={item.clear}
        />
      </span>
    </Group>
  );
}

function Toggle({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key] ?? item.value ?? false;

  return (
    <Group
      item={item}
      className={classNames(
        props.offsetToggle && 'offset-toggle-group',
        item.title && 'titled-toggle-group',
      )}
    >
      {item.title && <label className="form-label">{item.title}</label>}
      <div className="form-toggle-container">
        <div className="form-toggle-label">{item.label}</div>
        <div className="form-toggle-action">
          <label className="form-toggle">
            <input
              className="form-toggle-input"
              type="checkbox"
              checked={value === true}
              onChange={handleCheckboxChange.bind(null, item, props.onChange)}
            />
            <span className="form-toggle-switch" />
          </label>
        </div>
      </div>
    </Group>
  );
}

function DateForm({ item, ...props }) {
  const data = props.data || {};
  const current = data[item.key] ?? item.value ?? null;

  return (
    <Group item={item}>
      <label className="form-label">{item.label}</label>
      <div className="form">
        <Month
          noLabel
          item={item}
          {...props}
          onChange={handleDateChange.bind(null, item, 'month', current, props.onChange)}
        />
        <Year
          noLabel
          item={item}
          {...props}
          onChange={handleDateChange.bind(null, item, 'year', current, props.onChange)}
        />
      </div>
    </Group>
  );
}

function Month({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key]?.month ?? item.value?.month ?? '';
  const monthOptions = useMemo(
    () =>
      [{ value: '', label: 'MM' }].concat(
        Object.entries(months).map(([value]) => ({
          value,
          label: value.length === 1 ? `0${value}` : value,
        })),
      ),
    [],
  );
  const selectedOption = useMemo(
    () => monthOptions.find((option) => option.value == value),
    [value, monthOptions],
  );

  const onChange = (option) => {
    if (option.value == '') {
      props.onChange(item.key, null);
    } else {
      props.onChange(item.key, { month: Number.parseInt(option.value), year: null });
    }
  };

  return (
    <Group item={item}>
      {props.noLabel ? null : <label className="form-label">{item.label}</label>}
      <span className="form-select-container">
        <Select
          className={styles.select}
          value={selectedOption}
          options={monthOptions}
          placeholder="MM"
          onChange={onChange}
        />
      </span>
    </Group>
  );
}

function Year({ item, ...props }) {
  const data = props.data || {};
  const value = data[item.key]?.year ?? item.value?.year ?? '';
  const yearOptions = useMemo(
    () =>
      [{ value: '', label: 'YYYY' }].concat(
        yearRange.map((year) => ({ value: year, label: year })),
      ),
    [],
  );
  const selectedOption = useMemo(
    () => yearOptions.find((option) => option.value == value),
    [value, yearOptions],
  );

  const onChange = (option) => {
    if (option.value == '') {
      props.onChange(item.key, null);
    } else {
      props.onChange(item.key, { month: null, year: Number.parseInt(option.value) });
    }
  };

  return (
    <Group item={item}>
      {props.noLabel ? null : <label className="form-label">{item.label}</label>}
      <span className="form-select-container">
        <Select
          className={styles.select}
          value={selectedOption}
          options={yearOptions}
          placeholder="YYYY"
          onChange={onChange}
        />
      </span>
    </Group>
  );
}

function handleInputChange(item, onChange, e) {
  return onChange(item.key, e.currentTarget.value);
}

function handleCheckboxChange(item, onChange, e) {
  return onChange(item.key, e.currentTarget.checked);
}

function handleDateChange(item, type, data, onChange, _key, value) {
  let date = {
    month: data?.month ?? null,
    year: data?.year ?? null,
  };

  value = value || {};
  date[type] = value[type] ?? null;

  if (!date.month && !date.year) {
    date = null;
  }

  return onChange(item.key, date);
}
